import {
	useConfirmProductImageUploadMutation,
	useCreateProductMutation,
	useGenerateProductImageUploadUrlMutation,
} from '@codezo/graphql';
import { ArrowLeft, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CreateBrandStep } from '../../components/brands/CreateBrandStep';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { AnimatedHeight } from '../../components/ui/animated-height';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from '../../components/ui/dialog';
import { ImageCropper, type ImageCropCoords } from '../../components/ui/image-cropper';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';
import { Tabs } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { SpecificationsEditor } from '../../components/products/specifications/SpecificationsEditor';
import {
	countSpecEntries,
	emptySpecForm,
	formToSpecInput,
	type SpecFormState,
} from '@codezo/product-specs';
import type { CreatedBrand } from '../../hooks/useCreateBrand';
import { StagedImage, stageFiles, uploadProductImage } from '../../lib/product-image-upload';
import { cn } from '../../lib/utils';
import { ProductImageStager } from './ProductImageStager';

type SubmitPhase = 'idle' | 'creating' | 'uploading';

export function CreateProductModal({
	open,
	onOpenChange,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}) {
	const [tab, setTab] = useState('details');
	const [name, setName] = useState('');
	const [sku, setSku] = useState('');
	const [description, setDescription] = useState('');
	const [brandId, setBrandId] = useState('');
	const [brandLabel, setBrandLabel] = useState('');
	/* Inline "create brand" step — replaces the form, like the crop view. */
	const [brandStep, setBrandStep] = useState<{ seed: string } | null>(null);
	const [dimLength, setDimLength] = useState('');
	const [dimWidth, setDimWidth] = useState('');
	const [dimHeight, setDimHeight] = useState('');
	const [dimUnit, setDimUnit] = useState('cm');
	const [weight, setWeight] = useState('');
	const [specs, setSpecs] = useState<SpecFormState>(emptySpecForm());
	const [images, setImages] = useState<StagedImage[]>([]);
	const [phase, setPhase] = useState<SubmitPhase>('idle');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [cropIndex, setCropIndex] = useState<number | null>(null);

	const [createProduct] = useCreateProductMutation();
	const [generateUploadUrl] = useGenerateProductImageUploadUrlMutation();
	const [confirmUpload] = useConfirmProductImageUploadMutation();

	const busy = phase !== 'idle';

	/* ── Staging ── */

	const addFiles = (files: File[]) => {
		setImages((prev) => [...prev, ...stageFiles(files)]);
	};

	const removeImage = (index: number) => {
		setImages((prev) => {
			const next = [...prev];
			if (next[index].preview) URL.revokeObjectURL(next[index].preview);
			next.splice(index, 1);
			return next;
		});
	};

	const handleSaveCrop = (coords: ImageCropCoords) => {
		if (cropIndex === null) return;
		setImages((prev) =>
			prev.map((img, i) => (i === cropIndex ? { ...img, crop: coords } : img))
		);
		setCropIndex(null);
	};

	const cropImage = cropIndex === null ? null : images[cropIndex];

	/* Newly created brand becomes the selection right away (list refetch not needed). */
	const handleBrandCreated = (brand?: CreatedBrand) => {
		if (brand) {
			setBrandId(brand.id);
			setBrandLabel(brand.name);
		}
		setBrandStep(null);
	};

	const resetForm = () => {
		images.forEach((img) => img.preview && URL.revokeObjectURL(img.preview));
		setTab('details');
		setName('');
		setSku('');
		setDescription('');
		setBrandId('');
		setBrandLabel('');
		setBrandStep(null);
		setDimLength('');
		setDimWidth('');
		setDimHeight('');
		setDimUnit('cm');
		setWeight('');
		setSpecs(emptySpecForm());
		setImages([]);
		setPhase('idle');
		setUploadProgress(0);
		setCropIndex(null);
	};

	const handleOpenChange = (next: boolean) => {
		if (busy) return; // don't allow closing mid-submit
		if (!next) resetForm();
		onOpenChange(next);
	};

	const closeAfterSuccess = () => {
		resetForm();
		onOpenChange(false);
		onSuccess?.();
	};

	/* ── Submit: create product, then upload staged images ── */

	const handleSubmit = async () => {
		if (!name.trim() || busy) return;

		const hasDimensions = !!(dimLength || dimWidth || dimHeight);

		setPhase('creating');
		try {
			const { data } = await createProduct({
				variables: {
					input: {
						name: name.trim(),
						sku: sku.trim() || undefined,
						description: description.trim() || undefined,
						brandId: brandId || undefined,
						weight: weight ? parseFloat(weight) : undefined,
						dimensions: hasDimensions
							? {
									length: dimLength ? parseFloat(dimLength) : 0,
									width: dimWidth ? parseFloat(dimWidth) : 0,
									height: dimHeight ? parseFloat(dimHeight) : 0,
									unit: dimUnit,
								}
							: undefined,
						specifications: formToSpecInput(specs) ?? undefined,
					},
				},
			});

			if (!data?.createProduct?.success) {
				toast.error(data?.createProduct?.message || 'Failed to create product');
				setPhase('idle');
				return;
			}

			const newProductId = data.createProduct.referenceKey ?? undefined;

			/* No images to attach → done. */
			if (images.length === 0) {
				toast.success('Product created');
				closeAfterSuccess();
				return;
			}

			/* Product created but we have no id to attach images to. */
			if (!newProductId) {
				toast.success('Product created');
				toast.error('Images could not be attached — add them from the product page.');
				closeAfterSuccess();
				return;
			}

			/* Upload staged images to the new product. */
			setPhase('uploading');
			setUploadProgress(0);
			const total = images.length;
			try {
				for (let i = 0; i < total; i++) {
					await uploadProductImage({
						productId: newProductId,
						image: images[i],
						isPrimary: i === 0,
						generateUploadUrl,
						confirmUpload,
						onProgress: (fraction) => setUploadProgress(((i + fraction) / total) * 100),
					});
				}
				toast.success('Product created');
				closeAfterSuccess();
			} catch (err) {
				toast.success('Product created');
				toast.error(
					err instanceof Error
						? `Some images failed to upload: ${err.message}`
						: 'Some images failed to upload'
				);
				closeAfterSuccess();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create product');
			setPhase('idle');
		}
	};

	const submitLabel =
		phase === 'creating'
			? 'Creating…'
			: phase === 'uploading'
				? 'Uploading…'
				: 'Create product';

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-lg" showCloseButton={false}>
				<AnimatedHeight step={cropImage ? 'crop' : brandStep ? 'brand' : tab}>
					{brandStep ? (
						/* Create-brand view (replaces the form — no modal-on-modal) */
						<CreateBrandStep
							initialName={brandStep.seed}
							onCancel={() => setBrandStep(null)}
							onCreated={handleBrandCreated}
						/>
					) : cropImage ? (
						<>
							{/* Crop view (replaces the form — no modal-on-modal) */}
							<div className="flex shrink-0 items-center gap-2 px-5 pt-5 pb-3">
								<button
									onClick={() => setCropIndex(null)}
									className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
								>
									<ArrowLeft size={15} />
								</button>
								<DialogTitle className="text-lg font-medium text-text-1">
									Crop image
								</DialogTitle>
							</div>
							<ImageCropper
								key={`staged-${cropIndex}`}
								imageSrc={cropImage.preview}
								aspect={1}
								hideRotate
								restrictPosition
								saveLabel="Save crop"
								initialCrop={cropImage.crop ?? null}
								onCancel={() => setCropIndex(null)}
								onSaveCrop={handleSaveCrop}
							/>
						</>
					) : (
						<>
							{/* Header + Tabs */}
							<div className="shrink-0">
								<div className="flex items-start justify-between px-5 pt-5 pb-3">
									<div>
										<DialogTitle className="text-lg font-medium text-text-1">
											Add product
										</DialogTitle>
										<p className="mt-1 text-sm text-text-3">
											Create a new product and attach images.
										</p>
									</div>
									<button
										onClick={() => handleOpenChange(false)}
										disabled={busy}
										className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
									>
										<X size={15} />
									</button>
								</div>
								<div className="px-5">
									<Tabs
										value={tab}
										onChange={setTab}
										items={[
											{ value: 'details', label: 'Details' },
											{
												value: 'specifications',
												label: 'Specifications',
												count: countSpecEntries(specs) || undefined,
											},
											{
												value: 'assets',
												label: 'Assets',
												count: images.length,
											},
										]}
									/>
								</div>
							</div>

							{/* Body */}
							<DialogBody className="scroll-thin max-h-[65vh] p-5">
								<div className="grid grid-cols-1 [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-w-0">
									{/* Details tab */}
									<div
										className={
											tab !== 'details'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<div className="flex flex-col gap-5">
											<div className="flex flex-col gap-1.5">
												<Label>
													Name <span className="text-destructive">*</span>
												</Label>
												<Input
													value={name}
													onChange={(e) => setName(e.target.value)}
													placeholder="e.g. Custom Tote Bag"
													maxLength={75}
													autoFocus
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>SKU</Label>
												<Input
													value={sku}
													onChange={(e) => setSku(e.target.value)}
													placeholder="e.g. PRD-001"
													maxLength={30}
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>Description</Label>
												<Textarea
													value={description}
													onChange={(e) => setDescription(e.target.value)}
													placeholder="Brief product description"
													maxLength={500}
													rows={3}
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>Brand</Label>
												<BrandSelector
													value={brandId}
													onChange={(next, label) => {
														setBrandId(next);
														setBrandLabel(label ?? '');
													}}
													clearable
													selectedLabel={
														brandId
															? brandLabel || undefined
															: undefined
													}
													placeholder="Select a brand (optional)"
													skip={!open}
													onCreateNew={(query) =>
														setBrandStep({ seed: query })
													}
												/>
											</div>

											{/* Dimensions */}
											<div>
												<Label className="mb-2">Dimensions</Label>
												<div className="grid grid-cols-3 gap-2">
													{(
														[
															['Length', dimLength, setDimLength],
															['Width', dimWidth, setDimWidth],
															['Height', dimHeight, setDimHeight],
														] as const
													).map(([label, value, setter]) => (
														<div
															key={label}
															className="flex flex-col gap-1"
														>
															<label className="text-xs text-text-3">
																{label}
															</label>
															<NumberInput
																value={value}
																onChange={(e) =>
																	setter(
																		(
																			e.target as HTMLInputElement
																		).value
																	)
																}
																placeholder="0"
																min={0}
																step={0.01}
															/>
														</div>
													))}
												</div>
											</div>

											{/* Unit */}
											<div className="flex flex-col gap-1.5">
												<Label>Unit</Label>
												<div className="flex gap-1.5">
													{(['cm', 'mm', 'in', 'm'] as const).map((u) => (
														<button
															key={u}
															type="button"
															onClick={() => setDimUnit(u)}
															className={cn(
																'flex h-8 cursor-pointer items-center justify-center rounded-lg border px-4 text-xs font-medium transition-colors',
																dimUnit === u
																	? 'border-accent bg-accent-soft text-accent'
																	: 'border-line-2 bg-transparent text-text-2 hover:border-line-3'
															)}
														>
															{u}
														</button>
													))}
												</div>
											</div>

											{/* Weight */}
											<div className="flex flex-col gap-1.5">
												<Label>Weight (kg)</Label>
												<NumberInput
													value={weight}
													onChange={(e) =>
														setWeight(
															(e.target as HTMLInputElement).value
														)
													}
													placeholder="0"
													min={0}
													step={0.01}
													className="max-w-[200px]"
												/>
											</div>
										</div>
									</div>

									{/* Specifications tab */}
									<div
										className={
											tab !== 'specifications'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<SpecificationsEditor
											value={specs}
											onChange={setSpecs}
											disabled={busy}
										/>
									</div>

									{/* Assets tab */}
									<div
										className={
											tab !== 'assets'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<ProductImageStager
											images={images}
											onAddFiles={addFiles}
											onRemove={removeImage}
											onCropImage={(index) => setCropIndex(index)}
											disabled={busy}
										/>

										{phase === 'uploading' && (
											<div className="mt-4 rounded-[var(--r-3)] border border-line-1 bg-surface-fill/60 px-3 py-3">
												<div className="mb-1.5 h-1 overflow-hidden rounded-full bg-surface-input">
													<div
														className="h-full rounded-full bg-accent transition-all duration-300"
														style={{ width: `${uploadProgress}%` }}
													/>
												</div>
												<div className="text-center text-xs text-text-4">
													Uploading images…
												</div>
											</div>
										)}
									</div>
								</div>
							</DialogBody>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={() => handleOpenChange(false)}
									disabled={busy}
								>
									Cancel
								</Button>
								<Button
									type="button"
									onClick={handleSubmit}
									disabled={!name.trim() || busy}
								>
									{submitLabel}
								</Button>
							</DialogFooter>
						</>
					)}
				</AnimatedHeight>
			</DialogContent>
		</Dialog>
	);
}
